import type { SoftphoneCallBannerProps } from './index.types';
import { observer } from 'mobx-react';
import { getIncomingCallAnnouncement } from 'Modules/softphone/constants/announcements';
import { useCallBannerDisplay } from 'Modules/softphone/hooks/useCallBannerDisplay';
import React, { useState } from 'react';
import { CallBannerActions } from './CallBannerActions';
import { CallBannerAvatar } from './CallBannerAvatar';
import { CallBannerInfo } from './CallBannerInfo';

/**
 * SoftphoneCallBanner displays an incoming call notification banner.
 *
 * The banner shows:
 * - Caller avatar (with intelligent fallback to initials or icon)
 * - Caller information (name, location, phone number, ring group)
 * - Action buttons (Accept/Decline)
 *
 * The hook is called once at this level and display data is passed down
 * to child components to avoid redundant hook executions.
 */
export const SoftphoneCallBanner: React.FC<SoftphoneCallBannerProps> = observer(
  ({ data }) => {
    const {
      avatarUrl,
      displayNameForAvatar,
      callerLabel,
      ringGroupName,
      callTypeLabel,
      showPhoneNumber,
      formattedPhoneNumber,
    } = useCallBannerDisplay(data);

    const { isVideo, onAccept, onDecline } = data;

    /**
     * One sentence, fixed at mount.
     *
     * The live region below is deliberately not this container: an assertive
     * region restarts on every mutation of its subtree, and this subtree keeps
     * moving — the avatar fallback lands ~600ms in, the dismiss countdown
     * re-renders every second, the contact lookup resolves whenever it does.
     * Reading the container also read both buttons, whose names carry the
     * caller.
     *
     * Lazy `useState` so a late-resolving name cannot restart the announcement.
     */
    const [announcement] = useState(
      () =>
        getIncomingCallAnnouncement({
          callerLabel,
          ringGroupName,
          phoneNumber: showPhoneNumber ? formattedPhoneNumber : undefined,
          isVideo,
        }).message
    );

    return (
      <div
        role="group"
        aria-label="Incoming call notification"
        className="flex flex-row justify-between gap-[12px] p-[12px] rounded-[4px] shadow-2xl border bg-[#F9F9F9] border-[#E6E6E6] min-w-[400px] max-w-[400px]"
      >
        <div role="alert" className="sr-only">
          {announcement}
        </div>

        <div className="flex items-start gap-[12px] flex-1 min-w-0">
          <CallBannerAvatar
            avatarUrl={avatarUrl}
            displayNameForAvatar={displayNameForAvatar}
            isVideo={isVideo}
            callerLabel={callerLabel}
          />
          <CallBannerInfo
            callTypeLabel={callTypeLabel}
            callerLabel={callerLabel}
            ringGroupName={ringGroupName}
            showPhoneNumber={showPhoneNumber}
            formattedPhoneNumber={formattedPhoneNumber}
          />
        </div>

        <CallBannerActions
          callerLabel={callerLabel}
          isVideo={isVideo}
          onAccept={onAccept}
          onDecline={onDecline}
        />
      </div>
    );
  }
);

SoftphoneCallBanner.displayName = 'SoftphoneCallBanner';
